import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, AlertCircle } from 'lucide-react';
import { WorkerProject, CalculatorResult } from '../types/worker.types';
import { WorkerLayout } from '../components/layout/WorkerLayout';
import { CalculatorTool } from '../components/modules/calculator/CalculatorTool';
import { CalculatorHistory } from '../components/modules/calculator/CalculatorHistory';
import { Card, CardContent } from '../components/ui';

interface WorkerCalculatorProps {
  project: WorkerProject;
  token: string;
}

const CALCULATOR_TOOLS = {
  pente: {
    title: 'Calcul de pente',
    description: 'Calculer la pente en pourcentage à partir de la hauteur et de la distance horizontale'
  },
  denivele: {
    title: 'Calcul de dénivelé',
    description: 'Calculer la différence d\'altitude entre deux points'
  },
  ngf: {
    title: 'Conversion NGF',
    description: 'Convertir une altitude relative en altitude NGF (Nivellement Général de la France)'
  }
};

export function WorkerCalculator({ project, token }: WorkerCalculatorProps) {
  const navigate = useNavigate();
  const [results, setResults] = useState<CalculatorResult[]>([]);
  const [activeTab, setActiveTab] = useState<'tools' | 'history'>('tools');

  const handleBack = () => {
    navigate(`/worker/${token}`);
  };

  useEffect(() => {
    const savedResults = localStorage.getItem(`calculator_history_${project.id}`);
    if (savedResults) {
      try {
        setResults(JSON.parse(savedResults));
      } catch (error) {
        console.error('Erreur lors du chargement de l\'historique:', error);
      }
    }
  }, [project.id]);

  const handleResultSave = (result: CalculatorResult) => {
    const updatedResults = [result, ...results].slice(0, 50); // Garder les 50 derniers
    setResults(updatedResults);
    localStorage.setItem(`calculator_history_${project.id}`, JSON.stringify(updatedResults));
  };

  const handleClearHistory = () => {
    setResults([]);
    localStorage.removeItem(`calculator_history_${project.id}`);
  };

  const enabledTools = project.config.modules.calculator.tools;

  if (!project.config.modules.calculator.enabled) {
    return (
      <WorkerLayout 
        project={project} 
        onBack={handleBack}
        showBackButton
        title="Calculatrice"
      >
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <p className="text-orange-700">
              Le module calculatrice n'est pas activé pour ce projet
            </p>
          </CardContent>
        </Card>
      </WorkerLayout>
    );
  }

  if (enabledTools.length === 0) {
    return (
      <WorkerLayout 
        project={project} 
        onBack={handleBack}
        showBackButton
        title="Calculatrice"
      >
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="text-center py-8">
            <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              Aucun outil de calcul n'est configuré pour ce projet
            </p>
          </CardContent>
        </Card>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout 
      project={project} 
      onBack={handleBack}
      showBackButton
      title="Calculatrice"
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('tools')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'tools'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Outils
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Historique
            {results.length > 0 && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                {results.length}
              </span>
            )}
          </button>
        </div>

        {/* Contenu */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            {enabledTools.map((tool) => (
              <CalculatorTool
                key={tool}
                tool={tool}
                title={CALCULATOR_TOOLS[tool].title}
                description={CALCULATOR_TOOLS[tool].description}
                onResultSave={handleResultSave}
              />
            ))}
          </div>
        )}

        {activeTab === 'history' && (
          <CalculatorHistory
            results={results}
            onClearHistory={handleClearHistory}
          />
        )}
      </div>
    </WorkerLayout>
  );
}